import React, { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Ruler, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useBlobImage } from "@/hooks/useBlobImage";
import api from "@/lib/api";

export const RulerCalibration = ({ open, onOpenChange, pratica }) => {
  const qc = useQueryClient();
  const [tipo, setTipo] = useState("concessione");
  const [points, setPoints] = useState([]);
  const [cm, setCm] = useState("");
  const imgRef = useRef(null);
  const path = pratica?.[`${tipo}_file`]?.storage_path;
  const url = useBlobImage(path);

  const onClick = (e) => {
    if (points.length >= 2) return;
    const rect = imgRef.current.getBoundingClientRect();
    const sx = imgRef.current.naturalWidth / rect.width;
    const sy = imgRef.current.naturalHeight / rect.height;
    setPoints([...points, { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy, dx: e.clientX - rect.left, dy: e.clientY - rect.top }]);
  };

  const pxDist = points.length === 2 ? Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) : 0;
  const pxPerCm = pxDist && Number(cm) > 0 ? pxDist / Number(cm) : 0;

  const mut = useMutation({
    mutationFn: async () => (await api.put(`/pratiche/${pratica.id}/calibration`, {
      tipo, pixel_distance: pxDist, real_cm: Number(cm),
      image_width: imgRef.current?.naturalWidth || 0, image_height: imgRef.current?.naturalHeight || 0,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pratica", pratica.id] }); toast.success("Calibrazione salvata"); onOpenChange(false); setPoints([]); setCm(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore calibrazione"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ruler-calibration-modal" className="max-w-3xl bg-[#121824] border-slate-800 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Ruler className="h-5 w-5 text-cyan-400" /> Calibrazione Righello · Scala 1:100</DialogTitle>
          <DialogDescription className="text-slate-400">
            Clicca due punti su un elemento di misura nota (quota, righello, muro) e inserisci la distanza in cm sul foglio. In scala 1:100, 1 cm su carta = 1 m reale.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          {["concessione", "catastale"].map((t) => (
            <button key={t} data-testid={`calib-tipo-${t}`} onClick={() => { setTipo(t); setPoints([]); }}
              className={`px-3 py-1.5 rounded-lg text-xs border ${tipo === t ? "border-cyan-500 text-cyan-300 bg-cyan-500/10" : "border-slate-700 text-slate-400"}`}>
              {t === "concessione" ? "Concessione" : "Catastale"}
            </button>
          ))}
        </div>

        <div className="relative rounded-lg overflow-hidden bg-[#0B0F17] border border-slate-800 min-h-[200px] flex items-center justify-center">
          {url ? (
            <div className="relative inline-block">
              <img ref={imgRef} src={url} alt="calibrazione" data-testid="calib-image" onClick={onClick} className="max-h-[380px] max-w-full cursor-crosshair select-none" draggable={false} />
              <svg className="absolute inset-0 pointer-events-none w-full h-full">
                {points.map((p, i) => <circle key={i} cx={p.dx} cy={p.dy} r="5" fill="#22D3EE" stroke="#0B0F17" strokeWidth="2" />)}
                {points.length === 2 && <line x1={points[0].dx} y1={points[0].dy} x2={points[1].dx} y2={points[1].dy} stroke="#22D3EE" strokeWidth="2" strokeDasharray="6 3" />}
              </svg>
            </div>
          ) : <p className="text-slate-500 text-sm p-6">Carica prima la planimetria {tipo} (immagine, non PDF)</p>}
        </div>

        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[11px] text-slate-500 uppercase tracking-wider">Distanza su carta (cm)</label>
            <input data-testid="calib-cm-input" type="number" step="0.1" min="0.1" value={cm} onChange={(e) => setCm(e.target.value)} placeholder="Es. 5"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 focus:border-cyan-500 outline-none text-sm" />
          </div>
          <div className="font-mono-t text-xs text-slate-400">
            <p data-testid="calib-px-distance">Punti: {points.length}/2 · {pxDist ? `${pxDist.toFixed(1)} px` : "—"}</p>
            <p data-testid="calib-result" className="text-cyan-300">{pxPerCm ? `${pxPerCm.toFixed(2)} px/cm → ${pxPerCm.toFixed(2)} px/m reale` : "—"}</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="calib-reset-btn" onClick={() => setPoints([])} className="px-3 py-2 rounded-lg bg-[#1A2332] border border-slate-700 text-sm"><RotateCcw className="h-4 w-4" /></button>
            <button data-testid="calib-save-btn" disabled={!pxPerCm || mut.isPending} onClick={() => mut.mutate()}
              className="flex-1 px-3 py-2 rounded-lg bg-cyan-500 text-slate-900 text-sm font-semibold disabled:opacity-40">Salva calibrazione</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
